import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-gym-guide/artifacts/gwvje8fa_IMG_20220211_145748_413%20%285%29.jpg";

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Preenche todos os campos');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login efetuado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1760706112663-1b3932597980?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxtYWxlJTIwcnVubmVyJTIwc3ByaW50aW5nJTIwbmlnaHQlMjB1cmJhbnxlbnwwfHx8fDE3NzIwMTc4OTJ8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-3 brand-lockup" data-testid="login-brand-logo">
            <img src={LOGO_URL} alt="Process Body" className="w-12 h-12 rounded-full object-cover" />
            <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              PROCESS<span className="text-[#32CD32]">BODY</span>
            </span>
          </Link>
          <div>
            <h2 
              className="text-4xl font-bold tracking-tight uppercase mb-4"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Bem-vindo de <span className="text-[#32CD32]">volta</span>
            </h2>
            <p className="text-gray-400">
              Continua a tua jornada de treino personalizado com IA.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 pb-24 lg:pb-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-12 flex justify-center">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Process Body" className="w-14 h-14 rounded-full object-cover" />
              <span className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                PROCESS<span className="text-[#32CD32]">BODY</span>
              </span>
            </Link>
          </div>

          <div className="mb-8">
            <h1 
              className="text-3xl font-bold tracking-tight uppercase mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Entrar
            </h1>
            <p className="text-gray-400">
              Acede à tua conta para ver os teus planos de treino
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-300">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-gray-300">
                  Palavra-passe
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#32CD32] hover:underline"
                  data-testid="login-forgot-password-link"
                >
                  Esqueceste-te da palavra-passe?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 h-14 bg-[#121212] border-white/10 rounded-xl text-white placeholder:text-gray-500"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-14 rounded-xl text-lg"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  A entrar...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-gray-400">
            Não tens uma conta?{' '}
            <Link to="/register" className="text-[#32CD32] hover:underline font-medium" data-testid="register-link">
              Criar conta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
